import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { 
  QrCode, Users, Clock, CheckCircle, XCircle, Smartphone, Laptop, Tablet, 
  Monitor, Phone, Mail, MessageSquare, Loader2, ArrowRight, Download, Copy,
  RefreshCw, AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DEVICE_ICONS = {
  mobile: Smartphone,
  laptop: Laptop,
  tablet: Tablet,
  desktop: Monitor,
  other: Smartphone,
};

export default function CustomerIntakes() {
  const { token, user } = useAuth();
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchIntakes();
  }, [activeTab]);

  const fetchIntakes = async () => {
    setLoading(true);
    try {
      const status = activeTab === "all" ? "" : activeTab;
      const res = await axios.get(`${API}/api/customer-intakes${status ? `?intake_status=${status}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntakes(res.data.intakes);
    } catch (err) {
      toast.error("Failed to load customer intakes");
    } finally {
      setLoading(false);
    }
  };

  const fetchQrCode = async () => {
    setQrLoading(true);
    try {
      const subdomain = user?.tenant?.subdomain || localStorage.getItem("subdomain");
      const res = await axios.get(`${API}/api/public/shop/${subdomain}/qr-code`);
      setQrCode(res.data);
      setShowQrModal(true);
    } catch (err) {
      toast.error("Failed to generate QR code");
    } finally {
      setQrLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!selectedIntake) return;
    setConverting(true);
    try {
      const res = await axios.post(
        `${API}/api/customer-intakes/${selectedIntake.id}/convert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Job ${res.data.job_number} created successfully!`);
      setShowConvertModal(false);
      setSelectedIntake(null);
      fetchIntakes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert to job");
    } finally {
      setConverting(false);
    }
  };

  const handleReject = async (intakeId) => {
    try {
      await axios.post(
        `${API}/api/customer-intakes/${intakeId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Intake rejected");
      fetchIntakes();
    } catch (err) {
      toast.error("Failed to reject intake");
    }
  };

  const copyIntakeUrl = () => {
    if (qrCode?.intake_url) {
      navigator.clipboard.writeText(qrCode.intake_url);
      toast.success("URL copied to clipboard!");
    }
  };

  const downloadQr = () => {
    if (qrCode?.qr_code) {
      const link = document.createElement("a");
      link.href = qrCode.qr_code;
      link.download = `qr-code-${qrCode.shop_name || "shop"}.png`;
      link.click();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getContactIcon = (method) => {
    switch (method) {
      case "whatsapp": return MessageSquare;
      case "email": return Mail;
      default: return Phone;
    }
  };

  const pendingCount = intakes.filter(i => i.status === "pending").length;

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Self-Intake</h1>
            <p className="text-gray-600">Customers scan QR code and fill their details</p>
          </div>
          <Button onClick={fetchQrCode} disabled={qrLoading} className="bg-blue-600 hover:bg-blue-700">
            {qrLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
            Get QR Code
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-700">{pendingCount}</p>
                <p className="text-sm text-yellow-600">Pending Intakes</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {intakes.filter(i => i.status === "converted").length}
                </p>
                <p className="text-sm text-gray-500">Converted to Jobs</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{intakes.length}</p>
                <p className="text-sm text-gray-500">Total Submissions</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending" className="relative">
              Pending
              {pendingCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="converted">Converted</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : intakes.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <QrCode className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Intakes Yet</h3>
                  <p className="text-gray-500 mb-4">
                    Share your QR code with customers to receive self-intake submissions
                  </p>
                  <Button onClick={fetchQrCode} disabled={qrLoading}>
                    <QrCode className="w-4 h-4 mr-2" />
                    Generate QR Code
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {intakes.map((intake) => {
                  const DeviceIcon = DEVICE_ICONS[intake.device_type] || Smartphone;
                  const ContactIcon = getContactIcon(intake.preferred_contact);
                  
                  return (
                    <Card key={intake.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                          {/* Customer & Device Info */}
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <DeviceIcon className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{intake.customer_name}</h3>
                                <p className="text-sm text-gray-500 flex items-center gap-2">
                                  <ContactIcon className="w-3 h-3" />
                                  {intake.customer_phone}
                                </p>
                              </div>
                              <Badge 
                                className={
                                  intake.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                                  intake.status === "converted" ? "bg-green-100 text-green-700" :
                                  "bg-gray-100 text-gray-700"
                                }
                              >
                                {intake.status}
                              </Badge>
                            </div>
                            
                            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                              <p className="text-sm">
                                <span className="text-gray-500">Device:</span>{" "}
                                <span className="font-medium">{intake.device_brand} {intake.device_model || intake.device_type}</span>
                              </p>
                              <p className="text-sm">
                                <span className="text-gray-500">Problem:</span>{" "}
                                <span className="text-gray-900">{intake.problem_description}</span>
                              </p>
                              {intake.imei_serial && (
                                <p className="text-sm">
                                  <span className="text-gray-500">IMEI/Serial:</span>{" "}
                                  <span className="font-mono text-xs">{intake.imei_serial}</span>
                                </p>
                              )}
                            </div>
                            
                            <p className="text-xs text-gray-400">
                              Submitted {formatDate(intake.created_at)}
                            </p>
                          </div>

                          {/* Actions */}
                          {intake.status === "pending" && (
                            <div className="flex gap-2 lg:flex-col">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700 flex-1"
                                onClick={() => {
                                  setSelectedIntake(intake);
                                  setShowConvertModal(true);
                                }}
                              >
                                <ArrowRight className="w-4 h-4 mr-1" />
                                Convert to Job
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:bg-red-50 flex-1"
                                onClick={() => handleReject(intake.id)}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                          
                          {intake.status === "converted" && intake.converted_job_id && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.location.href = `/jobs/${intake.converted_job_id}`}
                            >
                              View Job
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* QR Code Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Your Shop QR Code</DialogTitle>
            <DialogDescription>
              Print this QR code and display it in your shop. Customers can scan it to submit their device details.
            </DialogDescription>
          </DialogHeader>
          
          {qrCode && (
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center">
                <img src={qrCode.qr_code} alt="Shop QR Code" className="w-48 h-48" />
                <p className="mt-3 text-sm font-medium text-gray-900">{qrCode.shop_name}</p>
                <p className="text-xs text-gray-500">Scan to submit repair request</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Intake Form URL:</p>
                <div className="flex items-center gap-2">
                  <Input value={qrCode.intake_url} readOnly className="text-sm" />
                  <Button size="sm" variant="outline" onClick={copyIntakeUrl}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button className="flex-1" onClick={downloadQr}>
                  <Download className="w-4 h-4 mr-2" />
                  Download QR
                </Button>
                <Button variant="outline" onClick={() => setShowQrModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert to Job Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Job</DialogTitle>
            <DialogDescription>
              Create a new repair job from this customer intake.
            </DialogDescription>
          </DialogHeader>
          
          {selectedIntake && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p><strong>Customer:</strong> {selectedIntake.customer_name}</p>
                <p><strong>Phone:</strong> {selectedIntake.customer_phone}</p>
                <p><strong>Device:</strong> {selectedIntake.device_brand} {selectedIntake.device_model}</p>
                <p><strong>Problem:</strong> {selectedIntake.problem_description}</p>
                {selectedIntake.device_password && (
                  <p><strong>Password:</strong> {selectedIntake.device_password}</p>
                )}
                {selectedIntake.accessories && (
                  <p><strong>Accessories:</strong> {selectedIntake.accessories}</p>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowConvertModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleConvert} disabled={converting}>
                  {converting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Create Job
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
